<script setup>
// Regua de acoes da conversa: responsavel, time, prioridade e etiquetas.
//
// Cada gatilho abre EXATAMENTE o que o rotulo promete. Antes os tres abriam o
// mesmo painel com cinco campos, e mudar prioridade custava: clicar, ler cinco
// secoes, achar a certa, abrir o dropdown, escolher. Aqui custa dois cliques e
// nenhuma leitura — escolher ja aplica e fecha, porque e uma decisao so e
// confirmar decisao unica e atrito puro.
//
// As acoes usam os MESMOS dispatches do ConversationAction (setCurrentChat* +
// assign*), entao o comportamento e identico ao que ja rodava em producao.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useTrack } from 'dashboard/composables';
import { toast } from 'dashboard/composables/useToast';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { CONVERSATION_PRIORITY } from 'shared/constants/messages';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { CHANNEL_ICON_MAP } from 'dashboard/store/modules/unifiedView';
import { getChannelColor } from 'dashboard/components-next/icon/provider';
import { getContrastingTextColor } from '@chatwoot/utils';

const props = defineProps({
  conversation: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();
const { agentsList } = useAgentsList();
const teams = useMapGetter('teams/getTeams');
const currentUser = useMapGetter('getCurrentUser');
const {
  accountLabels,
  activeLabels,
  inactiveLabels,
  addLabelToConversation,
  removeLabelFromConversation,
} = useConversationLabels();

// Times e etiquetas sao carregados pela navegacao principal, mas o painel pode
// montar antes disso (link direto para a conversa). Busca defensiva: so dispara
// se a lista estiver vazia.
onMounted(() => {
  if (!teams.value?.length) store.dispatch('teams/get').catch(() => {});
  if (!accountLabels.value?.length)
    store.dispatch('labels/get').catch(() => {});
});

const PRIORITY_DOTS = {
  urgent: 'bg-n-ruby-9',
  high: 'bg-n-amber-9',
  medium: 'bg-n-blue-9',
  low: 'bg-n-slate-8',
};

const conversationId = computed(() => props.conversation?.id);
const assignee = computed(() => props.conversation?.meta?.assignee || null);
const team = computed(() => props.conversation?.meta?.team || null);
const priority = computed(() => props.conversation?.priority || null);

const openPop = ref(null);
const forwardInboxes = ref([]);
const search = ref('');
const popRef = ref(null);

onClickOutside(popRef, () => {
  openPop.value = null;
});

const carregarCaixas = async () => {
  try {
    forwardInboxes.value = await store.dispatch(
      'getForwardableInboxes',
      conversationId.value
    );
  } catch {
    forwardInboxes.value = [];
  }
};

const toggle = qual => {
  openPop.value = openPop.value === qual ? null : qual;
  search.value = '';
  // a lista depende da conversa; busca ao abrir, nao no mount
  if (qual === 'encaminhar') carregarCaixas();
  // Atribuiveis: o composable busca UMA vez por caixa na montagem — se o
  // agente abria o popover com a busca ainda em voo (ou depois de uma falha),
  // via so o "Ninguem" e nada tentava de novo. Rebuscar ao abrir e barato e
  // idempotente; o spinner cobre a janela.
  if (qual === 'agente' && props.conversation?.inbox_id) {
    store
      .dispatch('inboxAssignableAgents/fetch', [props.conversation.inbox_id])
      .catch(() => {});
  }
};

// Chamado pelo item "Encaminhar" do menu do cabecalho.
defineExpose({ abrirEncaminhar: () => toggle('encaminhar') });
const close = () => {
  openPop.value = null;
};

const priorityLabel = computed(() =>
  priority.value
    ? t(`CONVERSATION.PRIORITY.OPTIONS.${priority.value.toUpperCase()}`)
    : ''
);
const priorityDot = computed(
  () => PRIORITY_DOTS[priority.value] || 'bg-n-slate-6'
);

const casaBusca = texto =>
  !search.value.trim() ||
  String(texto || '')
    .toLowerCase()
    .includes(search.value.trim().toLowerCase());

// agentsList ja traz o item "Ninguem" (id 0) do proprio composable
const agentesFiltrados = computed(() =>
  (agentsList.value || []).filter(a => casaBusca(a.name))
);
// Membro da caixa primeiro; quem entra por enxergar todas as conversas vem
// depois, sob um rotulo. Sem a marca (lista antiga em cache) conta como membro.
const agentesDaCaixa = computed(() =>
  agentesFiltrados.value.filter(a => a.id === 0 || a.inbox_member !== false)
);
const agentesDeFora = computed(() =>
  agentesFiltrados.value.filter(a => a.id !== 0 && a.inbox_member === false)
);
const assignableUiFlags = useMapGetter('inboxAssignableAgents/getUIFlags');
const isLoadingAgents = computed(
  () =>
    assignableUiFlags.value?.isFetching && agentesFiltrados.value.length <= 1
);

const timesFiltrados = computed(() =>
  (teams.value || []).filter(tm => casaBusca(tm.name))
);
const etiquetasDisponiveis = computed(() =>
  (inactiveLabels.value || []).filter(l => casaBusca(l.title))
);

const PRIORIDADES = [
  CONVERSATION_PRIORITY.URGENT,
  CONVERSATION_PRIORITY.HIGH,
  CONVERSATION_PRIORITY.MEDIUM,
  CONVERSATION_PRIORITY.LOW,
  null,
];

const definirAgente = agente => {
  close();
  const alvo = agente?.id ? agente : null;
  store
    .dispatch('assignAgent', {
      conversationId: conversationId.value,
      agentId: alvo?.id || null,
      agent: alvo,
    })
    .then(() => toast.success(t('CONVERSATION.CHANGE_AGENT')))
    .catch(erro =>
      toast.error(
        erro?.response?.data?.message || t('CONVERSATION.CHANGE_AGENT_FAILED')
      )
    );
};

const atribuirAMim = () => {
  const eu = currentUser.value;
  definirAgente({ id: eu.id, name: eu.name, thumbnail: eu.avatar_url });
};

const definirTime = novoTime => {
  close();
  store.dispatch('setCurrentChatTeam', {
    team: novoTime,
    conversationId: conversationId.value,
  });
  store
    .dispatch('assignTeam', {
      conversationId: conversationId.value,
      teamId: novoTime?.id || 0,
    })
    .then(() => toast.success(t('CONVERSATION.CHANGE_TEAM')))
    .catch(() => toast.error(t('CONVERSATION.CHANGE_TEAM_FAILED')));
};

const definirPrioridade = nova => {
  close();
  const anterior = priority.value;
  store.dispatch('setCurrentChatPriority', {
    priority: nova,
    conversationId: conversationId.value,
  });
  store
    .dispatch('assignPriority', {
      conversationId: conversationId.value,
      priority: nova,
    })
    .then(() => {
      useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
        oldValue: anterior,
        newValue: nova,
        from: 'Conversation Sidebar',
      });
    })
    .catch(() =>
      toast.error(t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.FAILED'))
    );
};

const rotuloPrioridade = valor =>
  t(`CONVERSATION.PRIORITY.OPTIONS.${(valor || 'none').toUpperCase()}`);

const caixasFiltradas = computed(() =>
  (forwardInboxes.value || []).filter(i => casaBusca(i.name))
);

// Icone do canal na lista de encaminhar: reconhecer "e o WhatsApp da Asa
// Norte" pelo desenho e mais rapido que ler sete nomes parecidos
const iconeCaixa = caixa =>
  CHANNEL_ICON_MAP[caixa.channel_type] || 'i-lucide-inbox';
const corCaixa = caixa => getChannelColor(caixa.channel_type, null);

const encaminhar = caixa => {
  close();
  if (!caixa?.id) return;

  const accountId = props.conversation?.account_id;
  store
    .dispatch('forwardToInbox', {
      conversationId: conversationId.value,
      inboxId: caixa.id,
    })
    .then(data => {
      toast.success(t('CONVERSATION_SIDEBAR.FORWARD_INBOX.SUCCESS_TITLE'), {
        detail: t('CONVERSATION_SIDEBAR.FORWARD_INBOX.SUCCESS_DETAIL', {
          inboxName: caixa.name,
          displayId: data.display_id,
        }),
        action: {
          label: t('CONVERSATION_SIDEBAR.FORWARD_INBOX.GO_TO_CONVERSATION'),
          // display_id, NAO o id interno: a URL usa o numero de exibicao
          to: `/app/accounts/${accountId}/conversations/${data.display_id}`,
        },
        duration: 6000,
      });
      // "Caixas de entrada ativas" nasce das conversas do contato — sem
      // refazer, a caixa nova so apareceria depois do F5.
      const contactId = props.conversation?.meta?.sender?.id;
      if (contactId) store.dispatch('unifiedView/initForContact', contactId);
    })
    .catch(() =>
      toast.error(t('CONVERSATION_SIDEBAR.FORWARD_INBOX.FAILED_TITLE'), {
        detail: t('CONVERSATION_SIDEBAR.FORWARD_INBOX.FAILED'),
      })
    );
};

const alternarEtiqueta = etiqueta => {
  addLabelToConversation({ title: etiqueta.title });
  close();
};
</script>

<template>
  <div class="relative">
    <!-- Linha 1: responsavel, time e prioridade. Cada um diz SOZINHO se esta
         cheio ou vazio — tracejado com "+" quando nao tem nada, solido com o
         valor em negrito quando tem. Antes os tres viviam numa peca so e o
         tracejado dependia dos dois primeiros estarem vazios ao mesmo tempo:
         com o time preenchido, o responsavel vazio ficava identico ao cheio. -->
    <div class="flex flex-wrap items-center gap-1.5 px-3 pb-2">
      <button
        class="flex min-w-0 max-w-full items-center gap-1.5 rounded-lg border px-2 py-1 text-[11.5px] transition-colors"
        :class="
          assignee
            ? 'border-n-teal-6 bg-gradient-to-r from-n-teal-3 to-n-teal-2 text-n-teal-12 hover:from-n-teal-4'
            : 'border-dashed border-n-weak text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12'
        "
        @click="toggle('agente')"
      >
        <Avatar
          v-if="assignee"
          :name="assignee.name"
          :src="assignee.thumbnail"
          :size="16"
          rounded-full
          class="shrink-0"
        />
        <span v-else class="shrink-0 font-bold leading-none">+</span>
        <b v-if="assignee" class="truncate font-semibold">
          {{ assignee.name }}
        </b>
        <span v-else class="truncate">
          {{ $t('CONVERSATION_SIDEBAR.QUICK.ASSIGN') }}
        </span>
      </button>

      <button
        class="flex min-w-0 max-w-full items-center gap-1.5 rounded-lg border px-2 py-1 text-[11.5px] transition-colors"
        :class="
          team
            ? 'border-n-teal-6 bg-gradient-to-r from-n-teal-3 to-n-teal-2 text-n-teal-12 hover:from-n-teal-4'
            : 'border-dashed border-n-weak text-n-slate-10 hover:bg-n-alpha-1 hover:text-n-slate-12'
        "
        @click="toggle('time')"
      >
        <span v-if="team" class="i-lucide-users size-3 shrink-0" />
        <span v-else class="shrink-0 font-bold leading-none">+</span>
        <b v-if="team" class="truncate font-semibold">
          {{ team.name }}
        </b>
        <span v-else class="truncate">
          {{ $t('CONVERSATION_SIDEBAR.QUICK.TEAM') }}
        </span>
      </button>

      <button
        class="flex shrink-0 items-center gap-1.5 rounded-lg border px-2 py-1 text-[11.5px] transition-colors hover:bg-n-alpha-1"
        :class="
          priority
            ? 'border-n-strong text-n-slate-12'
            : 'border-dashed border-n-weak text-n-slate-10 hover:text-n-slate-12'
        "
        @click="toggle('prioridade')"
      >
        <span class="size-1.5 shrink-0 rounded-full" :class="priorityDot" />
        <b v-if="priorityLabel" class="font-semibold text-n-slate-12">
          {{ priorityLabel }}
        </b>
        <span v-else>{{ $t('CONVERSATION_SIDEBAR.QUICK.PRIORITY') }}</span>
      </button>
    </div>

    <!-- Linha 2: etiquetas compactas, rolando na horizontal -->
    <div
      class="slim-scrollbar flex items-center gap-1 overflow-x-auto px-3 pb-2.5"
    >
      <span
        v-for="label in activeLabels"
        :key="label.id"
        class="group/tag inline-flex shrink-0 items-center gap-1 rounded py-px pl-1.5 pr-1 text-[10px] font-bold"
        :style="{
          backgroundColor: label.color,
          color: getContrastingTextColor(label.color),
        }"
      >
        {{ label.title }}
        <button
          class="opacity-0 transition-opacity group-hover/tag:opacity-100"
          @click="removeLabelFromConversation(label.title)"
        >
          <span class="i-lucide-x size-2.5 block" />
        </button>
      </span>
      <button
        class="inline-flex shrink-0 items-center gap-1 rounded border border-dashed border-n-weak px-1.5 py-px text-[10.5px] text-n-slate-9 transition-colors hover:border-n-slate-6 hover:text-n-slate-11"
        @click="toggle('etiqueta')"
      >
        <span class="i-lucide-plus size-2.5" />
        {{ $t('CONVERSATION_SIDEBAR.QUICK.LABEL') }}
      </button>
    </div>

    <!-- Popovers dirigidos -->
    <div
      v-if="openPop"
      ref="popRef"
      class="absolute z-40 max-h-[300px] min-w-[228px] max-w-[calc(100%-1.5rem)] overflow-y-auto rounded-xl bg-n-solid-1 p-1.5 shadow-lg outline outline-1 outline-n-container"
      :class="{
        // encaminhar e disparado pelo ⋯ do cabecalho: alinhar com o menu
        // (top-14 do container do cabecalho) e abrir para BAIXO, nao por cima
        'right-3 -top-1': openPop === 'encaminhar',
        'right-3 top-9': openPop === 'prioridade',
        'left-3 top-9': !['encaminhar', 'prioridade'].includes(openPop),
      }"
    >
      <div
        class="mb-1.5 flex items-center gap-1.5 border-b border-n-weak px-1.5 pb-2"
      >
        <span
          class="text-[10px] font-bold uppercase tracking-[0.06em] text-n-slate-10"
        >
          {{
            openPop === 'encaminhar'
              ? $t('CONVERSATION_SIDEBAR.FORWARD_INBOX.TITLE')
              : $t(`CONVERSATION_SIDEBAR.QUICK.POP_${openPop.toUpperCase()}`)
          }}
        </span>
        <button
          v-if="openPop === 'agente'"
          class="ml-auto text-[11px] font-semibold text-n-brand hover:underline"
          @click="atribuirAMim"
        >
          {{ $t('CONVERSATION_SIDEBAR.SELF_ASSIGN') }}
        </button>
      </div>

      <input
        v-if="openPop !== 'prioridade'"
        v-model="search"
        type="text"
        class="!mb-1.5 !h-7 w-full !text-xs"
        :placeholder="$t('CONVERSATION_SIDEBAR.QUICK.SEARCH')"
      />

      <template v-if="openPop === 'agente'">
        <div v-if="isLoadingAgents" class="py-3 text-center">
          <span
            class="i-lucide-loader-circle size-4 animate-spin inline-block text-n-slate-9"
          />
        </div>
        <button
          v-for="agente in agentesDaCaixa"
          :key="agente.id"
          class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-n-slate-11 transition-colors hover:bg-n-slate-3 hover:text-n-slate-12"
          @click="definirAgente(agente)"
        >
          <Avatar
            :name="agente.name"
            :src="agente.thumbnail"
            :size="20"
            rounded-full
          />
          <span class="truncate">{{ agente.name }}</span>
          <span
            v-if="assignee && assignee.id === agente.id"
            class="i-lucide-check ml-auto size-3.5 text-n-brand"
          />
        </button>
        <div
          v-if="agentesDeFora.length"
          class="mt-1.5 border-t border-n-weak px-2 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.06em] text-n-slate-10"
        >
          {{ $t('CONVERSATION_SIDEBAR.QUICK.OTHER_ACCESS') }}
        </div>
        <button
          v-for="agente in agentesDeFora"
          :key="`fora-${agente.id}`"
          class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-n-slate-11 transition-colors hover:bg-n-slate-3 hover:text-n-slate-12"
          @click="definirAgente(agente)"
        >
          <Avatar
            :name="agente.name"
            :src="agente.thumbnail"
            :size="20"
            rounded-full
          />
          <span class="truncate">{{ agente.name }}</span>
          <span
            v-if="assignee && assignee.id === agente.id"
            class="i-lucide-check ml-auto size-3.5 text-n-brand"
          />
        </button>
      </template>

      <template v-else-if="openPop === 'time'">
        <button
          v-for="item in timesFiltrados"
          :key="item.id"
          class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-n-slate-11 transition-colors hover:bg-n-slate-3 hover:text-n-slate-12"
          @click="definirTime(item)"
        >
          <span class="i-lucide-users size-3.5 shrink-0 text-n-slate-9" />
          <span class="truncate">{{ item.name }}</span>
          <span
            v-if="team && team.id === item.id"
            class="i-lucide-check ml-auto size-3.5 text-n-brand"
          />
        </button>
        <button
          v-if="team"
          class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-n-slate-10 transition-colors hover:bg-n-slate-3"
          @click="definirTime(null)"
        >
          <span class="i-lucide-x size-3.5 shrink-0" />
          {{ $t('CONVERSATION_SIDEBAR.QUICK.CLEAR') }}
        </button>
      </template>

      <template v-else-if="openPop === 'prioridade'">
        <button
          v-for="valor in PRIORIDADES"
          :key="valor || 'none'"
          class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-n-slate-11 transition-colors hover:bg-n-slate-3 hover:text-n-slate-12"
          @click="definirPrioridade(valor)"
        >
          <span
            class="size-2 shrink-0 rounded-full"
            :class="
              PRIORITY_DOTS[valor] || 'border border-dashed border-n-slate-8'
            "
          />
          {{ rotuloPrioridade(valor) }}
          <span
            v-if="priority === valor"
            class="i-lucide-check ml-auto size-3.5 text-n-brand"
          />
        </button>
      </template>

      <template v-else-if="openPop === 'encaminhar'">
        <button
          v-for="caixa in caixasFiltradas"
          :key="caixa.id"
          class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-n-slate-11 transition-colors hover:bg-n-slate-3 hover:text-n-slate-12"
          @click="encaminhar(caixa)"
        >
          <span
            :class="[iconeCaixa(caixa), corCaixa(caixa)]"
            class="size-3.5 shrink-0"
          />
          <span class="truncate">{{ caixa.name }}</span>
        </button>
        <p
          v-if="!caixasFiltradas.length"
          class="px-2 py-3 text-center text-[11px] text-n-slate-9"
        >
          {{ $t('CONVERSATION_SIDEBAR.FORWARD_INBOX.NO_RESULTS') }}
        </p>
      </template>

      <template v-else>
        <button
          v-for="label in etiquetasDisponiveis"
          :key="label.id"
          class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-n-slate-11 transition-colors hover:bg-n-slate-3 hover:text-n-slate-12"
          @click="alternarEtiqueta(label)"
        >
          <span
            class="size-2 shrink-0 rounded-full"
            :style="{ backgroundColor: label.color }"
          />
          <span class="truncate">{{ label.title }}</span>
        </button>
        <p
          v-if="!etiquetasDisponiveis.length"
          class="px-2 py-3 text-center text-[11px] text-n-slate-9"
        >
          {{ $t('CONVERSATION_SIDEBAR.QUICK.NO_LABELS') }}
        </p>
      </template>
    </div>
  </div>
</template>
